<template>
  <v-dialog v-model="visible" max-width="600" eager persistent>
    <v-card class="signature-card">
      <v-card-title class="d-flex align-center">
        <v-icon left color="#2D4B95">mdi-draw-pen</v-icon>
        Mi Firma Simple LaOT
        <v-spacer />
        <v-btn icon @click="$emit('close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-subtitle class="signature-hint mt-0">
        Esta firma se usará para firmar elementos autorizados dentro del panel.
      </v-card-subtitle>

      <v-card-text>
        <div ref="canvasWrap" class="signature-canvas-wrap" :class="{ 'has-signature': hasSignature }">
          <canvas
            ref="canvas"
            class="signature-canvas"
            @pointerdown="start"
            @pointermove="move"
            @pointerup="end"
            @pointercancel="end"
            @pointerleave="end"
          ></canvas>

          <div v-if="!hasSignature" class="signature-placeholder">
            <v-icon size="28" color="grey lighten-1">mdi-signature-freehand</v-icon>
            <span>Dibuja tu firma aquí</span>
          </div>
        </div>

        <div class="signature-status">
          <v-icon v-if="hasSignature" small color="success">mdi-check-circle</v-icon>
          <span v-if="hasSignature" class="success--text">Firma lista</span>
          <span v-else class="grey--text">Sin firmar</span>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text :disabled="!hasSignature" @click="onClear">
          <v-icon left small>mdi-eraser</v-icon>
          Limpiar
        </v-btn>
        <v-btn color="#2D4B95" :dark="hasSignature" :disabled="!hasSignature" @click="onSave">
          <v-icon left small>mdi-content-save</v-icon>
          Guardar
        </v-btn>
        <v-btn text @click="$emit('close')">Cancelar</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'SignaturePadModal',
  props: { visible: Boolean },
  data() {
    return {
      isDrawing: false,
      ctx: null,
      lastX: 0,
      lastY: 0,
      hasSignature: false,
      resizeObserver: null
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.hasSignature = false
        this.$nextTick(this.observeCanvasWrap)
      } else {
				this.onClear()
        this.teardownObserver()
      }
    }
  },
  methods: {
    observeCanvasWrap() {
      const wrap = this.$refs.canvasWrap
      if (!wrap) return

      // Evita observers duplicados si el modal se reabre
      this.teardownObserver()

      this.resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          const { width, height } = entry.contentRect
          if (width > 0 && height > 0) {
            this.setupCanvas(width, height)
          }
        }
      })
      this.resizeObserver.observe(wrap)
    },
    teardownObserver() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect()
        this.resizeObserver = null
      }
    },
    setupCanvas(width, height) {
      const canvas = this.$refs.canvas
      if (!canvas) return

      const dpr = window.devicePixelRatio || 1

      // Preserva el dibujo actual si el canvas cambia de tamaño (ej. resize de ventana)
      let previousImage = null
      if (canvas.width > 0 && canvas.height > 0) {
        previousImage = canvas.toDataURL()
      }

      canvas.width = width * dpr
      canvas.height = height * dpr
      canvas.style.width = width + 'px'
      canvas.style.height = height + 'px'

      const ctx = canvas.getContext('2d')
      ctx.scale(dpr, dpr)
      ctx.lineJoin = 'round'
      ctx.lineCap = 'round'
      ctx.lineWidth = 2
      ctx.strokeStyle = '#000'
      this.ctx = ctx

      ctx.fillStyle = '#fff'
      ctx.fillRect(0, 0, width, height)

      if (previousImage) {
        const img = new Image()
        img.onload = () => ctx.drawImage(img, 0, 0, width, height)
        img.src = previousImage
      }
    },
    getPos(e) {
      const rect = this.$refs.canvas.getBoundingClientRect()
      return { x: e.clientX - rect.left, y: e.clientY - rect.top }
    },
    start(e) {
      if (!this.ctx) return
      this.isDrawing = true
      this.$refs.canvas.setPointerCapture(e.pointerId)
      const { x, y } = this.getPos(e)
      this.lastX = x
      this.lastY = y
    },
    move(e) {
      if (!this.isDrawing || !this.ctx) return
      const { x, y } = this.getPos(e)
      this.ctx.beginPath()
      this.ctx.moveTo(this.lastX, this.lastY)
      this.ctx.lineTo(x, y)
      this.ctx.stroke()
      this.lastX = x
      this.lastY = y
      this.hasSignature = true
    },
    end() {
      this.isDrawing = false
    },
    onClear() {
      const c = this.$refs.canvas
      if (!c || !this.ctx) return
      const dpr = window.devicePixelRatio || 1
      this.ctx.clearRect(0, 0, c.width / dpr, c.height / dpr)
      this.ctx.fillStyle = '#fff'
      this.ctx.fillRect(0, 0, c.width / dpr, c.height / dpr)
      this.hasSignature = false
    },
    onSave() {
      if (!this.hasSignature) return
      const c = this.$refs.canvas
      c.toBlob(blob => {
        this.$emit('save', blob)
      }, 'image/png')
    }
  },
  beforeDestroy() {
    this.teardownObserver()
  }
}
</script>

<style scoped>
.signature-card {
  border-radius: 12px;
}
.signature-hint {
  padding-top: 0;
  padding-bottom: 8px;
  line-height: 1.4;
}
.signature-canvas-wrap {
  position: relative;
  width: 100%;
  height: 260px;
  border: 2px dashed #d0d0d0;
  border-radius: 8px;
  background: #fafafa;
  transition: border-color 0.2s ease;
  overflow: hidden;
}
.signature-canvas-wrap.has-signature {
  border-color: #bdbdbd;
  border-style: solid;
}
.signature-canvas {
  width: 100%;
  height: 100%;
  touch-action: none;
  display: block;
  cursor: crosshair;
}
.signature-placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: #9e9e9e;
  font-size: 13px;
  pointer-events: none;
}
.signature-status {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
  font-size: 13px;
}
</style>